import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { User } from '../models/User'
import { userSchema } from '../models/schema'
import {
  deleteUser as deleteUserAPI,
  fetchUserById,
  fetchUsers,
  updateLoyaltyTiersForUser,
  updateUser as updateUserAPI,
} from '../services/UserService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<User> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getUsersSuccess,
  setQuery: setUserQuery,
  reset: resetUserState,
  resetQueryAndIds: resetUserQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: User }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectUsers = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedUser = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectUserById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectUsersByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedUser =
  (user: User | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<User>(user)))

export const getUsers = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchUsers(params),
    [userSchema],
    slice.actions
  )

export const getUserWithId = (id: number) =>
  SBAPI.FetchDispatched<User>(fetchUserById(id), userSchema, slice.actions)

export const updateUser = (user: User) =>
  SBAPI.UpdateDispatched<User>(updateUserAPI(user), user, slice.actions)

export const deleteUser = (user: User) =>
  SBAPI.DestroyDispatched<User>(deleteUserAPI(user.id), user, slice.actions)

export const updateLoyaltyTiers = (user: User) =>
  SBAPI.UpdateDispatched<User>(
    updateLoyaltyTiersForUser(user.id, user.loyaltyTiers ?? []),
    user,
    slice.actions
  )
